import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import { Box, Checkbox, LinearProgress, MenuItem, Select, TextField } from '@mui/material'
import { ui } from '../../../utils/uiTokens'
import { capitalize } from '../../../utils/functions'
import { courseApiSlice } from '../../../features/course/courseSlice'
import { CRM_POLL, CrmIntakeView, CrmSubjectRef, CrmTrackView, crmApi } from '../../../features/crm/crmApi'
import {
    Card, Chip, Chips, CrmDialog, CrmTimer, DetailsButton, FieldLabel, Hint, InfoGrid, Lightbox, Muted, OutlineButton, Pill, PrimaryButton, TONES,
} from '../shared/crmUi'
import {
    DELETE_REASONS, RETURN_REASONS, STEP_NOS, STOP_REASONS, digits, addMonthKey, courseTitle, dictLabel, downscaleImage, fmtDate, formatPhone, genPassword, hm, isImageUrl,
    money, subjectTitle, subjectsLine, todayKey,
} from '../shared/format'
import { useCrmDict } from '../shared/dict'
import { trackDone } from '../shared/stages'
import IntakeInfo from '../shared/IntakeInfo'
import Timeline from '../shared/Timeline'
import Onboarding, { obStagesReady, useIntakePassword } from './Onboarding'
import { Tab } from './IntakesPage'

const DEFAULT_END = '2027-06-30'

const IntakeDetail: React.FC<{ id: number; onMoved: (tab: Tab, id: number) => void }> = ({ id, onMoved }) => {
    const { formatMessage, locale } = useIntl()
    const { data: x, isFetching } = crmApi.useGetIntakeQuery(id, { pollingInterval: CRM_POLL })
    const [zoom, setZoom] = useState<string[] | null>(null)
    const [password, setPassword] = useIntakePassword(id)

    if (!x || x.id !== id) return <Card>{isFetching ? <LinearProgress /> : null}</Card>

    const d = x.data
    const deleted = x.status === 'DELETED'
    const lnk = (url?: string | null, label?: string) =>
        url ? (
            <a href={url} target="_blank" rel="noreferrer" style={{ color: ui.pink, fontWeight: 600 }}>
                {label || url}
            </a>
        ) : (
            '-'
        )

    return (
        <Card>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    <Box sx={{ minWidth: 0 }}>
                        <Muted>{formatMessage({ id: 'crmRegisteredBy' }, { name: x.sellerName || '-', time: hm(x.createdAt) })}</Muted>
                        <Box sx={{ fontSize: '20px', fontWeight: 800, color: ui.ink, margin: '2px 0', overflowWrap: 'anywhere' }}>{d.studentName}</Box>
                        <Muted>
                            {x.courseId ? `${courseTitle(x, locale)} · ` : ''}
                            {subjectsLine(x.subjects, locale)}
                        </Muted>
                        {x.editedAfterApprove && (
                            <Pill tone="wait" sx={{ marginTop: '6px' }}>
                                ✎ {formatMessage({ id: 'crmEditedBySeller' })} · {hm(x.editedAt)}
                            </Pill>
                        )}
                    </Box>
                    {x.status === 'RETURNED' || deleted ? (
                        <Pill tone="bad">{formatMessage({ id: deleted ? 'crmDeleted' : 'crmReturn' })}</Pill>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                            <CrmTimer deadlineAt={x.moderatorDeadlineAt} closedAt={x.approvedAt} big />
                            <Muted>{formatMessage({ id: 'crmModDeadline' }, { time: hm(x.moderatorDeadlineAt) })}</Muted>
                        </Box>
                    )}
                </Box>

                <IntakeInfo intake={x} />

                {x.status === 'RETURNED' && (
                    <Box sx={retBox}>
                        <Box sx={boxTitle}>
                            ↩ {formatMessage({ id: 'crmReturn' })} · {hm(x.returnedAt)}
                        </Box>
                        <InfoGrid>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(90px, auto) 1fr', gap: '6px 12px', fontSize: '13px' }}>
                                <Box sx={{ color: ui.sub }}>{formatMessage({ id: 'crmReturnAmount' })}</Box>
                                <Box><b>{x.returnAmount ? money(x.returnAmount) : '-'}</b></Box>
                                <Box sx={{ color: ui.sub }}>{formatMessage({ id: 'crmReason' })}</Box>
                                <Box>{dictLabel(formatMessage, 'Ret', x.returnReason)}</Box>
                                <Box sx={{ color: ui.sub }}>{formatMessage({ id: 'crmExplanation' })}</Box>
                                <Box sx={{ overflowWrap: 'anywhere' }}>{x.returnText}</Box>
                                <Box sx={{ color: ui.sub }}>{formatMessage({ id: 'crmApplication' })}</Box>
                                <Box>
                                    {isImageUrl(x.returnFileUrl) || isImageUrl(x.returnFileName) ? (
                                        <img
                                            src={x.returnFileUrl}
                                            alt=""
                                            onClick={() => setZoom([x.returnFileUrl])}
                                            style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, cursor: 'zoom-in' }}
                                        />
                                    ) : (
                                        lnk(x.returnFileUrl, `📄 ${x.returnFileName || formatMessage({ id: 'crmFile' })}`)
                                    )}
                                </Box>
                                <Box sx={{ color: ui.sub }}>{formatMessage({ id: 'crmProcessedBy' })}</Box>
                                <Box>{x.returnedByName || formatMessage({ id: 'MODERATOR' })}</Box>
                            </Box>
                        </InfoGrid>
                        <Hint sx={{ marginTop: '8px' }}>{formatMessage({ id: 'crmReturnedHint' })}</Hint>
                    </Box>
                )}

                {deleted && (
                    <Box sx={retBox}>
                        <Box sx={boxTitle}>
                            🗑 {formatMessage({ id: 'crmDeleted' })} · {fmtDate(x.deletedAt)} {hm(x.deletedAt)}
                        </Box>
                        <Muted>
                            {formatMessage({ id: 'crmReason' })}: {dictLabel(formatMessage, 'Del', x.deleteReason)}
                            {x.deletedByName ? ` · ${x.deletedByName}` : ''}
                        </Muted>
                    </Box>
                )}
                {x.status === 'MODERATION' && (
                    <ApproveForm key={x.id} intake={x} password={password} onPassword={setPassword} onZoom={setZoom} onApproved={() => onMoved('ALL', x.id)} />
                )}
                {x.status === 'APPROVED' && (
                    <>
                        <ApprovedBox intake={x} />
                        <Box sx={{ ...actionBox, gap: 0 }}>
                            <Onboarding intake={x} password={password} onPassword={setPassword} onZoom={setZoom} />
                        </Box>
                    </>
                )}
                {x.status !== 'RETURNED' && !deleted && (
                    <ReturnForm
                        key={`r${x.id}`}
                        intake={x}
                        onReturned={() => onMoved('RETURNED', x.id)}
                        onStopped={() => onMoved('STOPPED', x.id)}
                        onDeleted={() => onMoved(x.status === 'MODERATION' ? 'NEW' : 'ALL', 0)}
                    />
                )}

                <Timeline intake={x} />
            </Box>
            <Lightbox urls={zoom} onClose={() => setZoom(null)} />
        </Card>
    )
}

const ApproveForm: React.FC<{ intake: CrmIntakeView; password: string; onPassword: (v: string) => void; onZoom: (u: string[]) => void; onApproved: () => void }> = ({
    intake,
    password,
    onPassword,
    onZoom,
    onApproved,
}) => {
    const { formatMessage, locale } = useIntl()
    const { data: courses = [] } = courseApiSlice.useFetchAllCoursesQuery()
    const [approve, { isLoading }] = crmApi.useApproveIntakeMutation()
    const [courseId, setCourseId] = useState<number | ''>('')
    const [endDate, setEndDate] = useState('')
    const [confirmed, setConfirmed] = useState<number[]>([])
    const [accessUntil, setAccessUntil] = useState(() => addMonthKey(todayKey()))
    const dict = useCrmDict()
    const inst = intake.installment || dict.isInstallment(intake.data.payType)

    const subjects = intake.subjects ?? []
    const confirmedTitles = subjects.filter((s) => confirmed.includes(s.id)).map((s) => subjectTitle(s, locale))
    const stagesOk = obStagesReady(intake)
    const ready = !!courseId && !!endDate && (!inst || !!accessUntil) && !!password && confirmed.length > 0 && stagesOk

    const submit = async () => {
        if (!ready) return
        try {
            await approve({
                id: intake.id,
                courseId: Number(courseId),
                endDate,
                subjectIds: confirmed,
                tempPassword: password,
                accessUntil: inst ? accessUntil : undefined,
            }).unwrap()
            toast.success(formatMessage({ id: 'crmApprovedToast' }))
            onApproved()
        } catch {
            /* общий обработчик */
        }
    }

    return (
        <Box sx={actionBox}>
            <Box sx={boxTitle}>{formatMessage({ id: 'crmApproveTitle' })}</Box>
            <Box sx={twoCols}>
                <Box>
                    <FieldLabel required>{formatMessage({ id: 'crmCourse' })}</FieldLabel>
                    <Select
                        size="small"
                        fullWidth
                        displayEmpty
                        value={courseId}
                        onChange={(e) => {
                            const v = Number(e.target.value) || ''
                            setCourseId(v)
                            setEndDate(v ? endDate || DEFAULT_END : '')
                        }}
                        renderValue={(v) => {
                            const c = courses.find((c) => c.id === v)
                            return c ? c[`title${capitalize(locale)}`] || c.titleRu : <Box component="span" sx={{ color: ui.muted }}>{formatMessage({ id: 'crmPickCourse' })}</Box>
                        }}
                    >
                        {courses.map((c) => (
                            <MenuItem key={c.id} value={c.id}>{c[`title${capitalize(locale)}`] || c.titleRu}</MenuItem>
                        ))}
                    </Select>
                </Box>
                {!!courseId && inst && (
                    <Box sx={{ background: TONES.wait.bg, borderRadius: '12px', padding: '10px' }}>
                        <FieldLabel required>{formatMessage({ id: 'crmAccessUntil' })}</FieldLabel>
                        <TextField type="date" size="small" fullWidth value={accessUntil} onChange={(e) => setAccessUntil(e.target.value)} sx={{ background: ui.card }} />
                        <Hint sx={{ marginTop: '4px' }}>
                            {formatMessage(
                                { id: 'crmAccessUntilHint' },
                                { type: dict.label('PAY_TYPE', intake.data.payType), sum: money(intake.nextPaySum ?? intake.data.nextPaySum) },
                            )}
                        </Hint>
                    </Box>
                )}
                {!!courseId && !inst && (
                    <Box>
                        <FieldLabel required>{formatMessage({ id: 'crmEndDate' })}</FieldLabel>
                        <TextField type="date" size="small" fullWidth value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                        <Hint sx={{ marginTop: '4px' }}>{formatMessage({ id: 'crmEndDateHint' })}</Hint>
                    </Box>
                )}
            </Box>

            <Box>
                <FieldLabel required hint={formatMessage({ id: 'crmConfirmSubjectsHint' })}>{formatMessage({ id: 'crmConfirmSubjects' })}</FieldLabel>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {subjects.map((s) => {
                        const on = confirmed.includes(s.id)
                        return (
                            <Box
                                key={s.id}
                                component="label"
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: 1,
                                    flexWrap: 'wrap',
                                    border: `1px solid ${on ? TONES.ok.border : ui.border}`,
                                    background: on ? TONES.ok.bg : ui.card,
                                    borderRadius: '10px',
                                    padding: '4px 10px 4px 2px',
                                    cursor: 'pointer',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Checkbox
                                        size="small"
                                        checked={on}
                                        onChange={(e) => setConfirmed((list) => (e.target.checked ? [...list, s.id] : list.filter((v) => v !== s.id)))}
                                    />
                                    <b style={{ fontSize: 13.5 }}>{subjectTitle(s, locale)}</b>
                                </Box>
                                <Pill tone={s.videoOnly ? 'grey' : 'wait'}>{formatMessage({ id: s.videoOnly ? 'crmVideoOnly' : 'crmGoesToHead' })}</Pill>
                            </Box>
                        )
                    })}
                </Box>
                <Hint sx={{ marginTop: '6px' }}>{formatMessage({ id: 'crmUnconfirmedHint' })}</Hint>
            </Box>

            <Onboarding intake={intake} password={password} onPassword={onPassword} onZoom={onZoom} />

            <Hint>
                {formatMessage({ id: 'crmAccessFor' })} <b>{confirmedTitles.join(', ') || formatMessage({ id: 'crmConfirmFirst' })}</b>.{' '}
                {formatMessage({ id: 'crmApproveExplain' })}
            </Hint>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', flexWrap: 'wrap', border: `2px solid ${ui.pinkSoft}`, borderRadius: '14px', padding: '14px', opacity: ready ? 1 : 0.7 }}>
                <Hint sx={{ flex: '1 1 260px' }}>{formatMessage({ id: ready ? 'crmApproveReady' : 'crmApproveCondition' })}</Hint>
                <PrimaryButton disabled={!ready || isLoading} onClick={submit}>
                    {formatMessage({ id: 'crmApproveTitle' })}
                </PrimaryButton>
            </Box>
        </Box>
    )
}

const ApprovedBox: React.FC<{ intake: CrmIntakeView }> = ({ intake: x }) => {
    const { formatMessage, locale } = useIntl()
    const [open, setOpen] = useState(false)
    const tracks = x.tracks ?? []
    const videoOnly = (x.subjects ?? []).filter((s) => s.videoOnly && (x.confirmedSubjectIds ?? []).includes(s.id))

    return (
        <Box sx={actionBox}>
            <Box sx={boxTitle}>
                ✓ {formatMessage({ id: 'crmApproved' })} · {hm(x.approvedAt)}
                {x.approvedByName ? ` · ${x.approvedByName}` : ''}
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '8px' }}>
                <Cred label={formatMessage({ id: 'crmCourse' })} value={courseTitle(x, locale)} />
                {x.installment ? (
                    <Cred label={formatMessage({ id: 'crmAccessUntilShort' })} value={x.accessUntil ? fmtDate(x.accessUntil) : formatMessage({ id: 'crmPayNoDate' })} />
                ) : (
                    <Cred label={formatMessage({ id: 'crmEndDate' })} value={fmtDate(x.endDate)} />
                )}
                <Cred label={formatMessage({ id: 'crmLogin' })} value={x.login || formatPhone(x.data.studentPhone)} />
                <Cred label={formatMessage({ id: 'crmTempPassword' })} value={x.tempPassword || formatMessage({ id: 'crmExistingAccount' })} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Muted sx={{ fontWeight: 700 }}>{formatMessage({ id: 'crmHeadAndMentor' })}</Muted>
                <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {tracks.map((t) => (
                        <Pill key={t.id} tone={trackDone(t) ? 'ok' : t.mentorId ? 'wait' : 'grey'}>
                            {subjectTitle(t.subject, locale)}:{' '}
                            {!t.mentorId
                                ? formatMessage({ id: 'crmAtHead' })
                                : trackDone(t)
                                ? formatMessage({ id: 'crmFinishedLower' })
                                : formatMessage({ id: 'crmMentorN' }, { done: t.doneCount })}
                        </Pill>
                    ))}
                    {videoOnly.map((s) => (
                        <Pill key={s.id} tone="ok">
                            {subjectTitle(s, locale)}: {formatMessage({ id: 'crmVideoOk' })}
                        </Pill>
                    ))}
                </Box>
                <DetailsButton open={open} onClick={() => setOpen(!open)} />
            </Box>
            {open && <DetailsTable intake={x} videoOnly={videoOnly} />}
        </Box>
    )
}

// «Детальдар»: строки - 5 задач, колонки - предметы, в ячейке только галочка, время - в подсказке
const DetailsTable: React.FC<{ intake: CrmIntakeView; videoOnly: CrmSubjectRef[] }> = ({ intake: x, videoOnly }) => {
    const { formatMessage, locale } = useIntl()
    const tracks = x.tracks ?? []
    const head = { padding: '8px 10px', borderBottom: `1px solid ${ui.border}`, textAlign: 'center' as const, verticalAlign: 'top' as const, minWidth: 130 }
    const cellSx = { padding: '8px 10px', borderBottom: `1px solid ${ui.border}`, textAlign: 'center' as const, verticalAlign: 'top' as const }
    const task = { padding: '8px 10px', borderBottom: `1px solid ${ui.border}`, fontSize: '13px', whiteSpace: 'nowrap' as const }

    const cell = (t: CrmTrackView, no: number) => {
        if (!t.mentorId) return <Check kind="none" title={formatMessage({ id: 'crmNoMentorYet' })} />
        const st = t.steps?.find((s) => s.stepNo === no)
        const n = st?.photos?.length ?? 0
        const title = st?.verifiedAt
            ? formatMessage({ id: 'crmHeadVerifiedAt' }, { time: hm(st.verifiedAt) })
            : st?.doneAt
            ? `${formatMessage({ id: 'crmUploadedAt' }, { time: hm(st.doneAt) })}${n > 1 ? ` · ${formatMessage({ id: 'crmPhotosN' }, { n })}` : ''} · ${formatMessage({ id: 'crmNotVerified' })}`
            : formatMessage({ id: 'crmNotUploadedCap' })
        return (
            <>
                <Check kind={st?.verifiedAt ? 'v' : st?.doneAt ? 'u' : ''} title={title} />
                {no === 5 && st?.doneAt && t.feedback && (
                    <Muted sx={{ marginTop: '4px' }}>
                        {dictLabel(formatMessage, 'Fb', t.feedback)}
                        {t.feedbackNote ? ` · ${t.feedbackNote}` : ''}
                    </Muted>
                )}
            </>
        )
    }

    return (
        <Box>
            {tracks.length > 0 && (
                <Box sx={{ overflowX: 'auto', border: `1px solid ${ui.border}`, borderRadius: '10px', background: ui.card }}>
                    <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%', fontSize: '13px' }}>
                        <thead>
                            <tr>
                                <Box component="th" sx={{ ...task, textAlign: 'left', color: ui.sub, fontWeight: 600 }}>{formatMessage({ id: 'crmTask' })}</Box>
                                {tracks.map((t) => (
                                    <Box component="th" key={t.id} sx={head}>
                                        <Box sx={{ fontWeight: 700, color: ui.ink }}>{subjectTitle(t.subject, locale)}</Box>
                                        <Muted>
                                            {formatMessage({ id: 'crmMentor' })}: <b style={{ color: ui.ink }}>{t.mentorName || '-'}</b>
                                        </Muted>
                                        <Muted>
                                            {formatMessage({ id: 'crmHeadShort' })}: <b style={{ color: ui.ink }}>{t.assignedByName || '-'}</b>
                                        </Muted>
                                    </Box>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {STEP_NOS.map((no) => (
                                <tr key={no}>
                                    <Box component="td" sx={task}>
                                        {no}. {formatMessage({ id: `crmStep_${no}` })}
                                    </Box>
                                    {tracks.map((t) => (
                                        <Box component="td" key={t.id} sx={cellSx}>
                                            {cell(t, no)}
                                        </Box>
                                    ))}
                                </tr>
                            ))}
                            <tr>
                                <Box component="td" sx={{ ...task, borderBottom: 'none' }}>{formatMessage({ id: 'crmVerifiedCol' })}</Box>
                                {tracks.map((t) => (
                                    <Box component="td" key={t.id} sx={{ ...cellSx, borderBottom: 'none' }}>
                                        <Pill tone={trackDone(t) ? 'ok' : 'grey'}>{t.verifiedCount}/5</Pill>
                                    </Box>
                                ))}
                            </tr>
                        </tbody>
                    </Box>
                </Box>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', marginTop: '8px', fontSize: '12px', color: ui.sub, alignItems: 'center' }}>
                {tracks.length > 0 && (
                    <>
                        <Legend kind="v" text={formatMessage({ id: 'crmLegendVerified' })} />
                        <Legend kind="u" text={formatMessage({ id: 'crmLegendUploaded' })} />
                        <Legend kind="" text={formatMessage({ id: 'crmNotUploadedCap' })} />
                        {tracks.some((t) => !t.mentorId) && <Legend kind="none" text={formatMessage({ id: 'crmNoMentorShort' })} />}
                    </>
                )}
                {videoOnly.length > 0 && (
                    <span>
                        {videoOnly.map((s) => subjectTitle(s, locale)).join(', ')} - {formatMessage({ id: 'crmVideoOnlyLower' })}
                    </span>
                )}
            </Box>
        </Box>
    )
}

type CheckKind = 'v' | 'u' | '' | 'none'

// Квадратик задачи: зелёный ✓ - проверено, жёлтый ✓ - загружено, пусто - нет, «–» - ментор не назначен
export const Check: React.FC<{ kind: CheckKind; title?: string; small?: boolean }> = ({ kind, title, small }) => {
    const size = small ? 16 : 22
    const tone = kind === 'v' ? TONES.ok : kind === 'u' ? TONES.wait : null
    return (
        <Box
            component="span"
            title={title}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: size,
                height: size,
                borderRadius: small ? '4px' : '6px',
                fontSize: small ? '11px' : '13px',
                fontWeight: 800,
                lineHeight: 1,
                background: tone ? tone.bg : kind === 'none' ? TONES.grey.bg : ui.card,
                color: tone ? tone.text : ui.muted,
                border: `1px solid ${tone ? tone.border : ui.border}`,
                flexShrink: 0,
            }}
        >
            {kind === 'v' || kind === 'u' ? '✓' : kind === 'none' ? '–' : ''}
        </Box>
    )
}

export const Legend: React.FC<{ kind: CheckKind; text: string }> = ({ kind, text }) => (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
        <Check kind={kind} small />
        {text}
    </Box>
)

const ReturnForm: React.FC<{ intake: CrmIntakeView; onReturned: () => void; onStopped: () => void; onDeleted: () => void }> = ({
    intake,
    onReturned,
    onStopped,
    onDeleted,
}) => {
    const { formatMessage } = useIntl()
    const [open, setOpen] = useState(false)
    const [stopOpen, setStopOpen] = useState(false)
    const [amount, setAmount] = useState('')
    const [resume, { isLoading: resuming }] = crmApi.useResumeIntakeMutation()
    const [deleting, setDeleting] = useState(false)
    const [reason, setReason] = useState('')
    const [text, setText] = useState('')
    const [file, setFile] = useState<File | null>(null)
    const [preview, setPreview] = useState('')
    const [zoom, setZoom] = useState<string[] | null>(null)
    const [doReturn, { isLoading }] = crmApi.useReturnIntakeMutation()
    const ok = !!reason && !!text.trim() && !!file && Number(amount) > 0

    if (stopOpen) return <StopForm intake={intake} onCancel={() => setStopOpen(false)} onStopped={onStopped} />

    if (!open && intake.stopped)
        return (
            <Box sx={stopBox}>
                <Box sx={{ ...boxTitle, color: TONES.wait.text }}>
                    ⏸ {formatMessage({ id: 'crmStoppedTitle' })}
                    {intake.stoppedAt ? ` · ${fmtDate(intake.stoppedAt)}` : ''}
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(90px, auto) 1fr', gap: '6px 12px', fontSize: '13px' }}>
                    <Box sx={{ color: ui.sub }}>{formatMessage({ id: 'crmReason' })}</Box>
                    <Box>{dictLabel(formatMessage, 'Stop', intake.stopReason)}</Box>
                    {!!intake.stopFrom && (
                        <>
                            <Box sx={{ color: ui.sub }}>{formatMessage({ id: 'crmStopPeriod' })}</Box>
                            <Box>
                                {fmtDate(intake.stopFrom)} - {intake.stopTo ? fmtDate(intake.stopTo) : formatMessage({ id: 'crmStopUnknown' })}
                            </Box>
                        </>
                    )}
                    {!!intake.stopNote && (
                        <>
                            <Box sx={{ color: ui.sub }}>{formatMessage({ id: 'crmExplanation' })}</Box>
                            <Box sx={{ overflowWrap: 'anywhere' }}>{intake.stopNote}</Box>
                        </>
                    )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <OutlineButton
                        disabled={resuming}
                        onClick={() =>
                            resume(intake.id)
                                .unwrap()
                                .then(() => toast.success(formatMessage({ id: 'crmResumedToast' })))
                                .catch(() => undefined)
                        }
                    >
                        ▶ {formatMessage({ id: 'crmResume' })}
                    </OutlineButton>
                    <OutlineButton color="error" onClick={() => setOpen(true)}>
                        ↩ {formatMessage({ id: 'crmReturn' })}
                    </OutlineButton>
                </Box>
            </Box>
        )

    if (!open)
        return (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <OutlineButton color="error" onClick={() => setOpen(true)}>
                    ↩ {formatMessage({ id: 'crmReturn' })}
                </OutlineButton>
                <OutlineButton onClick={() => setStopOpen(true)} sx={{ color: TONES.wait.text, borderColor: TONES.wait.border }}>
                    ⏸ {formatMessage({ id: 'crmStopStudy' })}
                </OutlineButton>
                <OutlineButton color="inherit" onClick={() => setDeleting(true)} sx={{ marginLeft: 'auto', color: ui.muted, borderColor: ui.border }}>
                    🗑 {formatMessage({ id: 'crmDeleteStudent' })}
                </OutlineButton>
                {deleting && <DeleteDialog intake={intake} onClose={() => setDeleting(false)} onDeleted={onDeleted} />}
            </Box>
        )

    const pickFile = async (f?: File) => {
        if (!f) return
        const small = await downscaleImage(f)
        setFile(small)
        setPreview(small.type.startsWith('image/') ? URL.createObjectURL(small) : '')
    }

    const submit = async () => {
        if (!ok) return
        const body = new FormData()
        body.append('reason', reason)
        body.append('amount', String(Number(amount)))
        body.append('text', text.trim())
        body.append('file', file)
        try {
            await doReturn({ id: intake.id, body }).unwrap()
            toast.success(formatMessage({ id: 'crmReturnedToast' }))
            onReturned()
        } catch {
            /* общий обработчик */
        }
    }

    return (
        <Box sx={retBox}>
            <Box sx={boxTitle}>↩ {formatMessage({ id: 'crmReturnTitle' })}</Box>
            <Box>
                <FieldLabel required>{formatMessage({ id: 'crmReason' })}</FieldLabel>
                <Chips>
                    {RETURN_REASONS.map((r) => (
                        <Chip key={r} tone="bad" on={reason === r} onClick={() => setReason(r)}>
                            {dictLabel(formatMessage, 'Ret', r)}
                        </Chip>
                    ))}
                </Chips>
            </Box>
            <Box sx={{ maxWidth: 280 }}>
                <FieldLabel required>{formatMessage({ id: 'crmReturnAmountLabel' })}</FieldLabel>
                <TextField
                    size="small"
                    fullWidth
                    value={amount}
                    placeholder={formatMessage({ id: 'crmReturnAmountPlaceholder' })}
                    inputProps={{ inputMode: 'numeric' }}
                    onChange={(e) => setAmount(digits(e.target.value))}
                    sx={{ background: ui.card }}
                />
                <Hint sx={{ marginTop: '4px' }}>{formatMessage({ id: 'crmReturnPaid' }, { sum: money(intake.data.paidSum) })}</Hint>
            </Box>
            <Box>
                <FieldLabel required>{formatMessage({ id: 'crmExplanation' })}</FieldLabel>
                <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={3}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={formatMessage({ id: 'crmReturnTextPlaceholder' })}
                    sx={{ background: ui.card }}
                />
            </Box>
            <Box>
                <FieldLabel required>{formatMessage({ id: 'crmApplicationFile' })}</FieldLabel>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <OutlineButton component="label">
                        📎 {formatMessage({ id: file ? 'crmReplace' : 'crmUploadApplication' })}
                        <input hidden type="file" accept="image/*,application/pdf" onChange={(e) => pickFile(e.target.files?.[0])} />
                    </OutlineButton>
                    {file ? (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', fontSize: '13px', minWidth: 0 }}>
                            {preview ? (
                                <img
                                    src={preview}
                                    alt=""
                                    onClick={() => setZoom([preview])}
                                    style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6, cursor: 'zoom-in' }}
                                />
                            ) : (
                                '📄'
                            )}
                            <Box component="span" sx={{ overflowWrap: 'anywhere' }}>{file.name}</Box>
                        </Box>
                    ) : (
                        <Hint>{formatMessage({ id: 'crmPhotoOrPdf' })}</Hint>
                    )}
                </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <PrimaryButton color="error" disabled={!ok || isLoading} onClick={submit}>
                    {formatMessage({ id: 'crmReturnTitle' })}
                </PrimaryButton>
                <OutlineButton onClick={() => setOpen(false)}>{formatMessage({ id: 'cancel' })}</OutlineButton>
            </Box>
            <Hint>{formatMessage({ id: 'crmReturnHint' })}</Hint>
            <Lightbox urls={zoom} onClose={() => setZoom(null)} />
        </Box>
    )
}

const StopForm: React.FC<{ intake: CrmIntakeView; onCancel: () => void; onStopped: () => void }> = ({ intake, onCancel, onStopped }) => {
    const { formatMessage } = useIntl()
    const [reason, setReason] = useState('')
    const [text, setText] = useState('')
    const [from, setFrom] = useState(todayKey())
    const [to, setTo] = useState('')
    const [stop, { isLoading }] = crmApi.useStopIntakeMutation()
    const ok = !!reason && !!text.trim()

    const submit = async () => {
        if (!ok) return
        try {
            await stop({ id: intake.id, reason, from, to, note: text.trim() }).unwrap()
            toast.success(formatMessage({ id: 'crmStoppedToast' }, { name: intake.data.studentName }))
            onStopped()
        } catch {
            /* общий обработчик */
        }
    }

    return (
        <Box sx={stopBox}>
            <Box sx={{ ...boxTitle, color: TONES.wait.text }}>⏸ {formatMessage({ id: 'crmStopStudy' })}</Box>
            <Box>
                <FieldLabel required>{formatMessage({ id: 'crmReason' })}</FieldLabel>
                <Chips>
                    {STOP_REASONS.map((r) => (
                        <Chip key={r} on={reason === r} onClick={() => setReason(r)}>
                            {dictLabel(formatMessage, 'Stop', r)}
                        </Chip>
                    ))}
                </Chips>
            </Box>
            <Box sx={twoCols}>
                <Box>
                    <FieldLabel>{formatMessage({ id: 'crmStopFrom' })}</FieldLabel>
                    <TextField type="date" size="small" fullWidth value={from} onChange={(e) => setFrom(e.target.value)} />
                </Box>
                <Box>
                    <FieldLabel hint={formatMessage({ id: 'crmStopToHint' })}>{formatMessage({ id: 'crmStopTo' })}</FieldLabel>
                    <TextField type="date" size="small" fullWidth value={to} onChange={(e) => setTo(e.target.value)} />
                </Box>
            </Box>
            <Box>
                <FieldLabel required>{formatMessage({ id: 'crmExplanation' })}</FieldLabel>
                <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={formatMessage({ id: 'crmStopTextPlaceholder' })}
                    sx={{ background: ui.card }}
                />
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <PrimaryButton disabled={!ok || isLoading} onClick={submit} sx={{ background: TONES.wait.text, '&:hover': { background: TONES.wait.text } }}>
                    {formatMessage({ id: 'crmStopStudy' })}
                </PrimaryButton>
                <OutlineButton onClick={onCancel}>{formatMessage({ id: 'cancel' })}</OutlineButton>
            </Box>
            <Hint>{formatMessage({ id: 'crmStopHint' })}</Hint>
        </Box>
    )
}

const DeleteDialog: React.FC<{ intake: CrmIntakeView; onClose: () => void; onDeleted: () => void }> = ({ intake, onClose, onDeleted }) => {
    const { formatMessage } = useIntl()
    const [reason, setReason] = useState('')
    const [ask, setAsk] = useState(false)
    const [remove, { isLoading }] = crmApi.useDeleteIntakeMutation()

    const submit = async () => {
        try {
            await remove({ id: intake.id, reason }).unwrap()
            toast.success(formatMessage({ id: 'crmDeletedToast' }, { name: intake.data.studentName }))
            onClose()
            onDeleted()
        } catch {
            /* общий обработчик */
        }
    }

    return (
        <CrmDialog title={formatMessage({ id: 'crmDeleteStudentTitle' })} sub={`${intake.data.studentName} · ${intake.sellerName || '-'}`} onClose={onClose}>
            <Hint sx={{ margin: '12px 0' }}>{formatMessage({ id: 'crmDeleteIntro' })}</Hint>
            <FieldLabel required>{formatMessage({ id: 'crmReason' })}</FieldLabel>
            <Chips>
                {DELETE_REASONS.map((r) => (
                    <Chip key={r} on={reason === r} onClick={() => setReason(r)}>
                        {dictLabel(formatMessage, 'Del', r)}
                    </Chip>
                ))}
            </Chips>
            <Hint sx={{ margin: '12px 0' }}>{formatMessage({ id: 'crmDeleteEffects' })}</Hint>
            {ask && <Box sx={{ color: TONES.bad.text, fontWeight: 600, fontSize: '13px', marginBottom: '10px' }}>{formatMessage({ id: 'crmDeleteSure' })}</Box>}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <OutlineButton onClick={() => (ask ? setAsk(false) : onClose())}>{formatMessage({ id: ask ? 'crmNo' : 'cancel' })}</OutlineButton>
                <PrimaryButton color="error" disabled={!reason || isLoading} onClick={() => (ask ? submit() : setAsk(true))}>
                    {formatMessage({ id: ask ? 'crmYesDelete' : 'crmDeleteBtn' })}
                </PrimaryButton>
            </Box>
        </CrmDialog>
    )
}

const Cred: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <Box sx={{ border: `1px solid ${ui.border}`, borderRadius: '10px', padding: '8px 12px', background: ui.card, minWidth: 0 }}>
        <Muted>{label}</Muted>
        <Box sx={{ fontWeight: 700, fontSize: '14px', overflowWrap: 'anywhere' }}>{value || '-'}</Box>
    </Box>
)

const actionBox = {
    border: `1px solid ${ui.border}`,
    background: ui.surface,
    borderRadius: '12px',
    padding: '14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
}
const stopBox = { ...actionBox, border: `2px solid ${TONES.wait.border}`, background: ui.card }
const retBox = { ...actionBox, border: `1px solid ${TONES.bad.border}`, background: TONES.bad.bg }
const boxTitle = { fontWeight: 700, fontSize: '15px', color: ui.ink }
const twoCols = { display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '12px' }
const cred = {
    border: `1px solid ${ui.border}`,
    borderRadius: '10px',
    padding: '6px 12px',
    fontFamily: 'monospace',
    fontSize: '15px',
    fontWeight: 700,
    background: ui.card,
}

export default IntakeDetail
